import { useCallback, useEffect, useMemo, useState } from 'react';

import { PAGE_SIZE_SM } from '~constants/common';

import dayjs, { dateInputFieldFormat } from '~libs/dayjs';
import { formatCompactCurrency } from '~libs/formatters';
import { useSearch } from '~libs/searchParams';

import { BreakdownItem } from '~components/visualisation/statistic/Statistic';

import {
  StatementBudgetsSummaryFilters,
  useGetStatementBudgetsSummaryQuery,
} from '~support-at-home/actions/statementBudgetsSummary';

interface BreakdownSet {
  opening: BreakdownItem[];
  spent: BreakdownItem[];
  closing: BreakdownItem[];
}

export type StatementsSearchValues = {
  statement_date?: { gte?: string; lte?: string };
  case_manager?: Array<number | string>;
  client_id?: Array<number | string>;
};

const currentMonthRange = () => ({
  gte: dayjs().startOf('month').format(dateInputFieldFormat),
  lte: dayjs().endOf('month').format(dateInputFieldFormat),
});

const joinIds = (ids?: Array<number | string>) => (ids && ids.length > 0 ? ids.join(',') : undefined);

/**
 * Maps the search bar's values onto the API's filters, because the API always needs a range. A
 * cleared start date falls back to the start of the end date's month (or the current month when
 * both are cleared), and a cleared end date to the end of the start date's month, so a fallback
 * can never produce a range that ends before it starts.
 */
export const toStatementFilters = (values: StatementsSearchValues): StatementBudgetsSummaryFilters => {
  const { gte, lte } = values.statement_date ?? {};
  const date_from = gte || (lte ? dayjs(lte).startOf('month').format(dateInputFieldFormat) : currentMonthRange().gte);
  const date_to = lte || dayjs(date_from).endOf('month').format(dateInputFieldFormat);
  return {
    date_from,
    date_to,
    client_ids: joinIds(values.client_id),
    case_manager_ids: joinIds(values.case_manager),
  };
};

export const useStatementBudgetSummary = (isActive: boolean) => {
  const [searchParams, setSearchParams] = useSearch('statements-dashboard-search', {
    statement_date: currentMonthRange(),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  const search = useCallback(
    (values: StatementsSearchValues) => {
      setSearchParams(values);
      setCurrentPage(1);
      setVisibleColumns([]);
    },
    [setSearchParams],
  );

  const filters = useMemo(() => toStatementFilters(searchParams ?? {}), [searchParams]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const queryArgs = useMemo(
    () => ({
      ...filters,
      limit: PAGE_SIZE_SM,
      offset: (currentPage - 1) * PAGE_SIZE_SM,
    }),
    [currentPage, filters],
  );

  const { data, currentData, isLoading, isFetching, isError, error } = useGetStatementBudgetsSummaryQuery(queryArgs, {
    skip: !isActive,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE_SM) : 0;

  // Fix #1: Wrap in useMemo to maintain stable reference when data is undefined
  const fundingTypes = useMemo(() => data?.summary?.funding_types ?? [], [data?.summary?.funding_types]);

  const allFundingTypeLabels = useMemo(() => fundingTypes.map((ft) => ft.budget_type_label), [fundingTypes]);

  const columnFilterItems = useMemo(
    () => fundingTypes.map((ft) => ({ key: ft.budget_type_label, label: ft.budget_type_label })),
    [fundingTypes],
  );

  // Fix #2: Include visibleColumns in dependency array
  useEffect(() => {
    if (allFundingTypeLabels.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(allFundingTypeLabels);
    }
  }, [allFundingTypeLabels, visibleColumns]);

  const visibleLabels = useMemo(() => (visibleColumns.length > 0 ? visibleColumns : null), [visibleColumns]);

  // Fix #6: Combine 3 breakdown useMemos into 1
  const breakdowns: BreakdownSet = useMemo(() => {
    const opening: BreakdownItem[] = [];
    const spent: BreakdownItem[] = [];
    const closing: BreakdownItem[] = [];
    for (const ft of fundingTypes) {
      const label = ft.budget_type_label;
      opening.push({ label, value: formatCompactCurrency(ft.total_opening_balance) });
      spent.push({ label, value: formatCompactCurrency(ft.total_spent) });
      closing.push({ label, value: formatCompactCurrency(ft.total_closing_balance) });
    }
    return { opening, spent, closing };
  }, [fundingTypes]);

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const totalCount = data?.total ?? 0;

  return {
    searchParams,
    search,
    filters,
    // currentData, not data: data holds the previous filters' result while new filters fetch
    exportRowCount: currentData?.summary?.total_export_rows,
    currentPage,
    totalPages,
    handlePageChange,
    visibleColumns,
    setVisibleColumns,
    columnFilterItems,
    visibleLabels,
    rows,
    summary,
    breakdowns,
    totalCount,
    isLoading,
    isFetching,
    isError,
    error,
  };
};
